// src/pages/dashboard/chapterPage/StepResultsPanel.jsx
import React, { Component } from 'react';
import { Icon } from '../../components/Icon';
import IssueCard from '../components/IssueCard';
import IssueFlowPanel from './IssueFlowPanel';
import { getAuthHeaders, urlMap } from '../../../utils';

const TASKS_TABS = [
  { key: 'plot', label: 'Plot' },
  { key: 'chapter', label: 'Chapter' },
  { key: 'showVsTell', label: 'Show vs Tell' },
  { key: 'pacing', label: 'Pacing' },
];

/**
 * Unified Tasks side-panel with tabs: Plot, Chapter, Show vs Tell, Pacing.
 * Also handles the standalone Hooks panel when stepName === 'ChapterHooksAndScenesStep'.
 *
 * Props:
 *  - stepName     : string  (e.g. "DetectChapterHolesStep" or "ChapterHooksAndScenesStep")
 *  - stepLabel    : string  (e.g. "Tasks" or "Hooks")
 *  - metadata     : object  (chapter.metadata)
 *  - chapter      : object
 *  - projectId    : string
 *  - projectIssues: array
 *  - onClose      : () => void
 */
export default class StepResultsPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      statusMap: StepResultsPanel.buildStatusMap(props.metadata),
      projectIssues: Array.isArray(props.projectIssues) ? props.projectIssues : [],
      activeTab: 'plot',
      hooksScenesTab: 'hooks',
      projectChapters: [],
      flowIssue: null,
    };
  }

  static buildStatusMap(metadata) {
    const statusMap = {};
    if (!metadata) return statusMap;
    for (const key of ['showVsTell', 'pacing', 'holes']) {
      (metadata[key]?.issues || []).forEach((item) => {
        if (item.id) statusMap[item.id] = item.status || 'unresolved';
      });
    }
    return statusMap;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.metadata !== this.props.metadata) {
      this.setState({ statusMap: StepResultsPanel.buildStatusMap(this.props.metadata) });
    }
    if (prevProps.projectIssues !== this.props.projectIssues) {
      this.setState({
        projectIssues: Array.isArray(this.props.projectIssues) ? this.props.projectIssues : [],
      });
    }
  }

  componentDidMount() {
    this.fetchProjectChapters();
  }

  async fetchProjectChapters() {
    const projectId = this.props.projectId || this.props.chapter?.project_id;
    if (!projectId) return;
    try {
      const res = await fetch(urlMap.getChapters(projectId), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const chapters = await res.json();
      this.setState({ projectChapters: Array.isArray(chapters) ? chapters : [] });
    } catch (err) {
      console.error('Failed to fetch project chapters for reassign', err);
    }
  }

  getStatus(id) {
    return this.state.statusMap[id] || 'unresolved';
  }

  async updateStatus(id, status) {
    const chapterId = this.props.chapter?.id;
    if (!chapterId || !id) return;

    // Optimistic update
    this.setState((prev) => ({
      statusMap: { ...prev.statusMap, [id]: status },
    }));

    try {
      await fetch(urlMap.updateMetadataItemStatus(chapterId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ id, status }),
      });
    } catch {
      // Revert on failure
      const revert = status === 'solved' ? 'unresolved' : 'solved';
      this.setState((prev) => ({
        statusMap: { ...prev.statusMap, [id]: revert },
      }));
    }
  }

  markSolved(id) {
    const current = this.getStatus(id);
    if (current === 'verified' || current === 'pending') return; // read-only / pending uses approve flow
    const next = current === 'solved' ? 'unresolved' : 'solved';
    this.updateStatus(id, next);
  }

  renderStatusButton(id) {
    const status = this.getStatus(id);
    if (status === 'verified') {
      return <span className="step-panel-status step-panel-status--verified">✓✓ Verified</span>;
    }
    if (status === 'pending') {
      return (
        <div className="step-panel-decision-btns">
          <button
            className="step-panel-approve-btn"
            onClick={() => this.approveSuggestion(id)}
            title="Approve — move to issues"
          >
            ✓ Approve
          </button>
          <button
            className="step-panel-dismiss-btn"
            onClick={() => this.dismissSuggestion(id)}
            title="Dismiss suggestion"
          >
            ✗ Dismiss
          </button>
        </div>
      );
    }
    return (
      <button
        className={`step-panel-status-btn ${status === 'solved' ? 'step-panel-status-btn--solved' : ''}`}
        onClick={() => this.markSolved(id)}
      >
        {status === 'solved' ? '✓ Solved' : '✓'}
      </button>
    );
  }

  async approveSuggestion(metadataId) {
    const projectId = this.props.projectId || this.props.chapter?.project_id;
    if (!projectId || !metadataId) return;

    // Optimistic: remove from list
    this.setState((prev) => ({
      statusMap: { ...prev.statusMap, [metadataId]: '_approving' },
    }));

    try {
      const response = await fetch(urlMap.approveSuggestion(projectId, metadataId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Approve failed');
      const issue = await response.json();

      // Add to project issues list so it appears on the Plot tab
      this.setState((prev) => ({
        statusMap: { ...prev.statusMap, [metadataId]: '_approved' },
        projectIssues: [issue, ...prev.projectIssues],
      }));

      // Notify parent to refresh chapter data
      if (this.props.onChapterRefresh) this.props.onChapterRefresh();
    } catch {
      this.setState((prev) => ({
        statusMap: { ...prev.statusMap, [metadataId]: 'pending' },
      }));
    }
  }

  async dismissSuggestion(metadataId) {
    const projectId = this.props.projectId || this.props.chapter?.project_id;
    if (!projectId || !metadataId) return;

    this.setState((prev) => ({
      statusMap: { ...prev.statusMap, [metadataId]: '_dismissing' },
    }));

    try {
      const response = await fetch(urlMap.dismissSuggestion(projectId, metadataId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Dismiss failed');

      this.setState((prev) => ({
        statusMap: { ...prev.statusMap, [metadataId]: '_dismissed' },
      }));

      if (this.props.onChapterRefresh) this.props.onChapterRefresh();
    } catch {
      this.setState((prev) => ({
        statusMap: { ...prev.statusMap, [metadataId]: 'pending' },
      }));
    }
  }

  getHoleLevelLabel(item) {
    const stage = item?.agentStage
      || (item?.source && String(item.source).includes('project') ? 'project_stage' : 'chapter_stage');
    return stage === 'project_stage' ? 'Plot' : 'Chapter';
  }

  getHoleLevelKey(item) {
    return this.getHoleLevelLabel(item) === 'Plot' ? 'plot' : 'chapter';
  }

  setActiveTab = (activeTab) => {
    this.setState({ activeTab });
  };

  getResultsFromMetadata() {
    const { metadata, stepName } = this.props;
    if (!metadata) return null;
    switch (stepName) {
      case 'DetectShowVsTellStep':
        return metadata.showVsTell || null;
      case 'DetectPacingIssuesStep':
        return metadata.pacing || null;
      case 'ChapterHooksAndScenesStep':
        return {
          hooks: metadata.hooks || null,
          scenes: metadata.scenes || null,
        };
      case 'DetectChapterHolesStep':
        return metadata.holes || null;
      default:
        return null;
    }
  }

  /* ── renderers per step type ── */

  renderShowVsTell(results) {
    const allIssues = results?.issues || results?.items || (Array.isArray(results) ? results : []);
    const issues = allIssues.filter((item) => !['_approved', '_dismissed', '_approving', '_dismissing'].includes(this.getStatus(item.id)));
    if (!issues.length) return <p className="step-panel-empty">No show-vs-tell issues found — nice work!</p>;

    return (
      <div className="step-panel-list">
        {issues.map((item, i) => {
          const status = this.getStatus(item.id);
          const issueObj = {
            id: item.id,
            title: item.issue || 'Show vs Tell issue',
            description: item.passage ? `**Passage:** ${item.passage}` : '',
            suggestion: item.suggestion || null,
            severity: item.severity || 'medium',
            issue_type: 'show_vs_tell',
            status,
          };
          return (
            <IssueCard
              key={item.id || i}
              issue={issueObj}
              onToggleStatus={(newStatus) => {
                const metaStatus = newStatus === 'done' ? 'solved' : 'unresolved';
                return this.updateStatus(item.id, metaStatus);
              }}
              onApprove={status === 'pending' ? () => this.approveSuggestion(item.id) : undefined}
              onDismiss={status === 'pending' ? () => this.dismissSuggestion(item.id) : undefined}
            />
          );
        })}
      </div>
    );
  }

  renderPacingIssues(results) {
    const allIssues = results?.issues || results?.items || (Array.isArray(results) ? results : []);
    const issues = allIssues.filter((item) => !['_approved', '_dismissed', '_approving', '_dismissing'].includes(this.getStatus(item.id)));
    if (!issues.length) return <p className="step-panel-empty">No pacing issues detected.</p>;

    return (
      <div className="step-panel-list">
        {issues.map((item, i) => {
          const status = this.getStatus(item.id);
          const descParts = [item.description, item.location ? `**Location:** ${item.location}` : ''].filter(Boolean);
          const issueObj = {
            id: item.id,
            title: item.description || 'Pacing issue',
            description: item.excerpt ? `**Excerpt:** ${item.excerpt}${descParts.length ? '\n\n' + descParts.join('\n\n') : ''}` : descParts.join('\n\n'),
            suggestion: item.suggestion || null,
            severity: item.severity || 'medium',
            issue_type: item.type || 'pacing',
            status,
          };
          return (
            <IssueCard
              key={item.id || i}
              issue={issueObj}
              onToggleStatus={(newStatus) => {
                const metaStatus = newStatus === 'done' ? 'solved' : 'unresolved';
                return this.updateStatus(item.id, metaStatus);
              }}
              onApprove={status === 'pending' ? () => this.approveSuggestion(item.id) : undefined}
              onDismiss={status === 'pending' ? () => this.dismissSuggestion(item.id) : undefined}
            />
          );
        })}
      </div>
    );
  }

  renderHooksAndScenes(results) {
    const hooks = results?.hooks || {};
    const scenes = results?.scenes || {};
    const scenesList = scenes?.scenes || [];
    const { hooksScenesTab } = this.state;

    return (
      <div className="step-panel-hooks-and-scenes">
        <div className="step-panel-tabs" role="tablist" aria-label="Hooks and scenes tabs">
          <button
            type="button"
            className={`step-panel-tab ${hooksScenesTab === 'hooks' ? 'step-panel-tab--active' : ''}`}
            onClick={() => this.setState({ hooksScenesTab: 'hooks' })}
            role="tab"
            aria-selected={hooksScenesTab === 'hooks'}
          >
            Hooks
          </button>
          <button
            type="button"
            className={`step-panel-tab ${hooksScenesTab === 'scenes' ? 'step-panel-tab--active' : ''}`}
            onClick={() => this.setState({ hooksScenesTab: 'scenes' })}
            role="tab"
            aria-selected={hooksScenesTab === 'scenes'}
          >
            Scenes ({scenes.totalScenes || scenesList.length})
          </button>
        </div>

        {hooksScenesTab === 'hooks' ? (
          <>
            <div className="step-panel-card step-panel-card--hook">
              <div className="step-panel-card-header">
                <span className="step-panel-badge step-panel-badge--hook">🪝 Opening</span>
                {hooks.opening?.rating && (
                  <span className={`step-panel-rating step-panel-rating--${hooks.opening.rating}`}>{hooks.opening.rating}</span>
                )}
              </div>
              {hooks.opening ? (
                <>
                  {hooks.opening.analysis && <p className="step-panel-explanation">{hooks.opening.analysis}</p>}
                  {hooks.opening.suggestion && (
                    <div className="step-panel-suggestion"><strong>Suggestion:</strong> {hooks.opening.suggestion}</div>
                  )}
                </>
              ) : (
                <p className="step-panel-empty">No opening hook analysis available.</p>
              )}
            </div>

            <div className="step-panel-card step-panel-card--hook">
              <div className="step-panel-card-header">
                <span className="step-panel-badge step-panel-badge--tension">⚡ Closing</span>
                {hooks.closing?.rating && (
                  <span className={`step-panel-rating step-panel-rating--${hooks.closing.rating}`}>{hooks.closing.rating}</span>
                )}
              </div>
              {hooks.closing ? (
                <>
                  {hooks.closing.analysis && <p className="step-panel-explanation">{hooks.closing.analysis}</p>}
                  {hooks.closing.suggestion && (
                    <div className="step-panel-suggestion"><strong>Suggestion:</strong> {hooks.closing.suggestion}</div>
                  )}
                </>
              ) : (
                <p className="step-panel-empty">No closing tension analysis available.</p>
              )}
            </div>

            {hooks.summary && <p className="step-panel-summary">{hooks.summary}</p>}
          </>
        ) : (
          <>
            {scenesList.length === 0 ? (
              <p className="step-panel-empty">No scenes extracted.</p>
            ) : (
              <div className="step-panel-list">
                {scenesList.map((scene, i) => (
                  <div key={i} className="step-panel-card step-panel-card--scene">
                    <div className="step-panel-card-header">
                      <span className="step-panel-badge step-panel-badge--scene">Scene {i + 1}</span>
                    </div>
                    {scene.title && <h5 className="step-panel-scene-title">{scene.title}</h5>}
                    {scene.summary && <p className="step-panel-explanation">{scene.summary}</p>}
                    {scene.characters && scene.characters.length > 0 && (
                      <p className="step-panel-meta"><strong>Characters:</strong> {scene.characters.join(', ')}</p>
                    )}
                    {scene.setting && <p className="step-panel-meta"><strong>Setting:</strong> {scene.setting}</p>}
                    {scene.purpose && <p className="step-panel-meta"><strong>Purpose:</strong> {scene.purpose}</p>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  /* ── plot issue handlers (called by IssueCard) ── */

  handlePlotIssueSave = async (issueId, { title, description }) => {
    const chapterId = this.props.chapter?.id;
    if (!chapterId) return;
    const response = await fetch(urlMap.updateChapterIssue(chapterId, issueId), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      credentials: 'include',
      body: JSON.stringify({ title, description }),
    });
    if (!response.ok) throw new Error('Failed to update issue');
    const updated = await response.json();
    this.setState((s) => ({
      projectIssues: s.projectIssues.map((i) => (i.id === issueId ? updated : i)),
    }));
  };

  handlePlotIssueStatusToggle = async (issueId, newStatus) => {
    const chapterId = this.props.chapter?.id;
    if (!chapterId) return;
    const response = await fetch(urlMap.updateChapterIssueStatus(chapterId, issueId), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      credentials: 'include',
      body: JSON.stringify({ status: newStatus }),
    });
    if (!response.ok) throw new Error('Failed to update status');
    const updated = await response.json();
    this.setState((s) => ({
      projectIssues: s.projectIssues.map((i) =>
        i.id === issueId ? { ...i, status: updated.status || newStatus } : i
      ),
    }));
  };

  handlePlotIssueDelete = async (issueId) => {
    const chapterId = this.props.chapter?.id;
    if (!chapterId) return;
    const response = await fetch(urlMap.deleteChapterIssue(chapterId, issueId), {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to delete issue');
    this.setState((s) => ({
      projectIssues: s.projectIssues.filter((i) => i.id !== issueId),
    }));
  };

  handleReassignIssue = async (issueId, targetChapterId) => {
    const chapterId = this.props.chapter?.id;
    if (!chapterId || !issueId || !targetChapterId) return;

    try {
      const res = await fetch(urlMap.reassignChapterIssue(chapterId, issueId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ target_chapter_id: targetChapterId }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // Remove the issue from current view
      this.setState((prev) => ({
        projectIssues: prev.projectIssues.filter((i) => i.id !== issueId),
      }));
    } catch (err) {
      console.error('Failed to reassign issue', err);
    }
  };

  renderChapterHoles(results) {
    const allChapterIssues = results?.issues || (Array.isArray(results) ? results : []);
    const chapterIssues = allChapterIssues.filter((item) => !['_approved', '_dismissed', '_approving', '_dismissing'].includes(this.getStatus(item.id)));

    if (!chapterIssues.length) return <p className="step-panel-empty">No chapter-level holes detected.</p>;

    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    const sortedChapterIssues = [...chapterIssues].sort((a, b) => (severityOrder[a.severity] ?? 9) - (severityOrder[b.severity] ?? 9));

    return (
      <div className="step-panel-list step-panel-list--holes">
        {sortedChapterIssues.map((item, origIdx) => {
          const status = this.getStatus(item.id);
          const issueObj = {
            id: item.id,
            title: item.description || 'Detected issue',
            description: item.evidence ? `**Evidence:** ${item.evidence}` : '',
            suggestion: item.suggestion || null,
            severity: item.severity || 'medium',
            issue_type: item.type || null,
            status,
          };
          return (
            <IssueCard
              key={item.id || origIdx}
              issue={issueObj}
              onToggleStatus={(newStatus) => {
                const metaStatus = newStatus === 'done' ? 'solved' : 'unresolved';
                return this.updateStatus(item.id, metaStatus);
              }}
              onApprove={status === 'pending' ? () => this.approveSuggestion(item.id) : undefined}
              onDismiss={status === 'pending' ? () => this.dismissSuggestion(item.id) : undefined}
            />
          );
        })}
        {results?.summary && <p className="step-panel-summary">{results.summary}</p>}
      </div>
    );
  }

  renderPlotIssues() {
    const projectIssues = this.state.projectIssues || [];
    const { projectChapters } = this.state;
    const currentChapterId = this.props.chapter?.id;
    const inProgressIssues = projectIssues.filter((i) => i.status === 'in_progress');

    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    const sorted = [...inProgressIssues].sort((a, b) => (severityOrder[a.severity] ?? 9) - (severityOrder[b.severity] ?? 9));

    if (!sorted.length) {
      const projectId = this.props.projectId || this.props.chapter?.project_id;
      return (
        <div className="step-panel-empty-state">
          <p>No in-progress issues for this chapter.</p>
          {projectId && (
            <p className="step-panel-empty-hint">
              <a href={`/project/${projectId}/library#issues`} className="step-panel-link">
                Open the Issues Board
              </a>{' '}
              to pick an issue to work on.
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="step-panel-list step-panel-list--holes">
        {sorted.map((issue) => (
          <IssueCard
            key={issue.id}
            issue={issue}
            onSave={({ title, description }) => this.handlePlotIssueSave(issue.id, { title, description })}
            onToggleStatus={(newStatus) => this.handlePlotIssueStatusToggle(issue.id, newStatus)}
            onDelete={() => this.handlePlotIssueDelete(issue.id)}
            chapters={projectChapters}
            currentChapterId={currentChapterId}
            onReassign={(targetChapterId) => this.handleReassignIssue(issue.id, targetChapterId)}
          />
        ))}
      </div>
    );
  }

  renderTabContent() {
    const { activeTab } = this.state;
    const { metadata } = this.props;

    switch (activeTab) {
      case 'plot':
        return this.renderPlotIssues();
      case 'chapter':
        return this.renderChapterHoles(metadata?.holes || {});
      case 'showVsTell': {
        const results = metadata?.showVsTell || null;
        if (!results) return <div className="step-panel-empty-state"><p>No results yet.</p><p className="step-panel-empty-hint">Run the chapter agent to generate analysis.</p></div>;
        return this.renderShowVsTell(results);
      }
      case 'pacing': {
        const results = metadata?.pacing || null;
        if (!results) return <div className="step-panel-empty-state"><p>No results yet.</p><p className="step-panel-empty-hint">Run the chapter agent to generate analysis.</p></div>;
        return this.renderPacingIssues(results);
      }
      default:
        return null;
    }
  }

  renderResults() {
    const { stepName } = this.props;
    const results = this.getResultsFromMetadata();

    // Tasks panel — show only in-progress issues for this chapter
    if (stepName === 'DetectChapterHolesStep') {
      return this.renderPlotIssues();
    }

    // Standalone Hooks panel (unchanged)
    if (!results || (stepName === 'ChapterHooksAndScenesStep' && !results.hooks && !results.scenes)) {
      return (
        <div className="step-panel-empty-state">
          <p>No results yet.</p>
          <p className="step-panel-empty-hint">
            Run the chapter agent to generate analysis for this step.
          </p>
        </div>
      );
    }

    switch (stepName) {
      case 'ChapterHooksAndScenesStep':
        return this.renderHooksAndScenes(results);
      default:
        return <pre className="step-panel-raw">{JSON.stringify(results, null, 2)}</pre>;
    }
  }

  render() {
    const { stepLabel, onClose, stepName } = this.props;
    const { flowIssue } = this.state;
    const panelTitle = stepName === 'ChapterHooksAndScenesStep' ? 'Hooks & Scenes' : stepLabel;

    if (flowIssue) {
      return (
        <div className="step-results-scroll">
          <aside className="step-results-side-panel" aria-label="Issue flow">
            <IssueFlowPanel
              issue={flowIssue}
              onClose={() => this.setState({ flowIssue: null })}
              onOpenInEditor={this.props.onOpenInEditor}
              onIssueResolved={(issueId) => {
                this.setState((s) => ({
                  flowIssue: null,
                  projectIssues: s.projectIssues.map((i) =>
                    i.id === issueId ? { ...i, status: 'done' } : i
                  ),
                }));
              }}
            />
          </aside>
        </div>
      );
    }

    return (
      <div className="step-results-scroll">
        <aside className="step-results-side-panel" aria-label={`${stepLabel} results`}>
          <div className="step-results-panel-header">
            <span className="step-results-panel-title">{panelTitle}</span>
            <button className="btn subtle" onClick={onClose} title="Close panel">
              <Icon.Close />
            </button>
          </div>
          <div className="step-results-panel-body">
            {this.renderResults()}
          </div>
        </aside>
      </div>
    );
  }
}
